import React, { useEffect, useMemo, useState, useCallback } from "react";
import {
  Stack, Typography, Paper, TextField, Button, Table, TableHead, TableBody,
  TableRow, TableCell, TableContainer, Chip, Box, InputAdornment, CircularProgress, Link,
} from "@mui/material";
import SyncIcon from "@mui/icons-material/SyncOutlined";
import SearchIcon from "@mui/icons-material/SearchOutlined";
import AccessTimeOutlinedIcon from "@mui/icons-material/AccessTimeOutlined";
import OpenInNewOutlinedIcon from "@mui/icons-material/OpenInNewOutlined";
import { apiGet, ApiError } from "../api/client";
import { useToast } from "../ui/Toaster";

const COLUMNS = [
  { key: "start_time",  header: "Starts",  width: 200 },
  { key: "city",        header: "City",    width: 140 },
  { key: "title",       header: "Title",   width: undefined },
  { key: "auction_id",  header: "Auction", width: 120 },
  { key: "open",        header: "",        width: 100, align: "right" },
];

export default function Upcoming() {
  const { error: toastError } = useToast();
  const [rows, setRows] = useState([]);
  const [q, setQ] = useState("");
  const [loading, setLoading] = useState(true);

  const fetchUpcoming = useCallback(async (signal) => {
    setLoading(true);
    try {
      const js = await apiGet("/api/wiz/upcoming", { signal });
      if (!js?.success) throw new ApiError(js?.error || "Upstream returned no data");
      const mapped = (js.results || []).map((a) => ({
        id: String(a.auction_id),
        auction_id: a.auction_id,
        city: a.city || "",
        title: a.title || "",
        start_time: a.start_time || "",
        url: a.url || `https://wiz.bid/search?auctionId=${a.auction_id}`,
      }));
      mapped.sort((x, y) => new Date(x.start_time).getTime() - new Date(y.start_time).getTime());
      setRows(mapped);
    } catch (e) {
      if (e?.name === "AbortError") return;
      toastError("Couldn't load upcoming auctions", e instanceof ApiError ? e.message : String(e));
    } finally {
      if (!signal?.aborted) setLoading(false);
    }
  }, [toastError]);

  useEffect(() => {
    const ctrl = new AbortController();
    fetchUpcoming(ctrl.signal);
    return () => ctrl.abort();
  }, [fetchUpcoming]);

  const filtered = useMemo(() => {
    const s = q.trim().toLowerCase();
    if (!s) return rows;
    return rows.filter((r) =>
      String(r.auction_id).toLowerCase().includes(s) ||
      r.city.toLowerCase().includes(s) ||
      r.title.toLowerCase().includes(s)
    );
  }, [rows, q]);

  return (
    <Box>
      {/* Page header — matches OC's standard layout (h1 serif title + secondary subtitle + right-aligned actions) */}
      <Stack
        direction={{ xs: 'column', sm: 'row' }}
        justifyContent="space-between"
        alignItems={{ xs: 'stretch', sm: 'flex-start' }}
        spacing={{ xs: 1.5, sm: 0 }}
        sx={{ mb: { xs: 2, md: 3.5 } }}
      >
        <Box>
          <Typography variant="h1" sx={{ mb: 0.5, fontSize: { xs: '1.85rem', md: '2.5rem' } }}>
            Upcoming Auctions
          </Typography>
          <Typography sx={{ color: 'text.secondary', fontSize: 14 }}>
            {filtered.length.toLocaleString()} auction{filtered.length === 1 ? "" : "s"} ahead
          </Typography>
        </Box>
        <Stack direction="row" spacing={1}>
          <Button
            startIcon={loading ? <CircularProgress size={15} color="inherit" /> : <SyncIcon />}
            variant="contained"
            onClick={() => fetchUpcoming()}
            disabled={loading}
          >
            Refresh
          </Button>
        </Stack>
      </Stack>

      {/* Filters card */}
      <Paper variant="outlined" sx={{ p: 2, mb: 2 }}>
        <Stack direction="row" gap={1.5} alignItems="center" flexWrap="wrap">
          <TextField
            placeholder="Search auction id, city, title…"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            size="small"
            sx={{ flex: 1, minWidth: 240 }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon fontSize="small" />
                </InputAdornment>
              ),
            }}
          />
        </Stack>
      </Paper>

      {/* Table */}
      <Paper variant="outlined" sx={{ overflow: 'hidden', position: 'relative' }}>
        <TableContainer>
          <Table size="small" sx={{ '& td, & th': { whiteSpace: 'nowrap' } }}>
            <TableHead>
              <TableRow>
                {COLUMNS.map((col) => (
                  <TableCell
                    key={col.key}
                    align={col.align || "left"}
                    style={col.width ? { width: col.width } : undefined}
                    sx={{ userSelect: 'none' }}
                  >
                    {col.header}
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {loading && filtered.length === 0 && (
                <TableRow>
                  <TableCell colSpan={COLUMNS.length} align="center" sx={{ py: 6, color: 'text.secondary' }}>
                    <Stack direction="row" alignItems="center" justifyContent="center" gap={1}>
                      <CircularProgress size={14} />
                      <span>Loading…</span>
                    </Stack>
                  </TableCell>
                </TableRow>
              )}
              {!loading && filtered.length === 0 && (
                <TableRow>
                  <TableCell colSpan={COLUMNS.length} align="center" sx={{ py: 6 }}>
                    <Stack alignItems="center" gap={1} sx={{ color: 'text.secondary' }}>
                      <AccessTimeOutlinedIcon fontSize="small" />
                      <Typography variant="body2" sx={{ fontWeight: 600 }}>No upcoming auctions</Typography>
                      <Typography variant="caption">
                        Auctions will appear here once scheduled.
                      </Typography>
                    </Stack>
                  </TableCell>
                </TableRow>
              )}
              {filtered.map((row) => {
                const d = new Date(row.start_time);
                const validDate = !isNaN(d.getTime());
                return (
                  <TableRow key={row.id} hover>
                    <TableCell sx={{ width: 200 }}>
                      {validDate ? (
                        <Box>
                          <Typography variant="body2">
                            {d.toLocaleDateString(undefined, { month: "short", day: "numeric", year: "2-digit" })}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            {d.toLocaleTimeString(undefined, { hour: "2-digit", minute: "2-digit" })}
                          </Typography>
                        </Box>
                      ) : (
                        <Typography variant="body2" color="text.secondary">{row.start_time}</Typography>
                      )}
                    </TableCell>
                    <TableCell sx={{ color: 'text.secondary' }}>{row.city || "—"}</TableCell>
                    <TableCell>
                      <Box sx={{ minWidth: 0, maxWidth: 480 }}>
                        <Link
                          href={row.url}
                          target="_blank"
                          rel="noreferrer"
                          underline="hover"
                          sx={{
                            color: 'text.primary',
                            display: 'inline-block',
                            maxWidth: '100%',
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap',
                            '&:hover': { color: 'primary.main' },
                          }}
                        >
                          {row.title || "(untitled)"}
                        </Link>
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Chip
                        size="small"
                        variant="outlined"
                        label={
                          <Box component="span" sx={{ fontFamily: 'mono', fontSize: '0.72rem' }}>
                            {row.auction_id}
                          </Box>
                        }
                      />
                    </TableCell>
                    <TableCell align="right">
                      <Button
                        size="small"
                        variant="text"
                        endIcon={<OpenInNewOutlinedIcon sx={{ fontSize: 14 }} />}
                        onClick={() => window.open(row.url, "_blank", "noreferrer")}
                      >
                        Open
                      </Button>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>
        {loading && filtered.length > 0 && (
          <Box sx={{
            position: 'absolute', top: 8, right: 12,
            display: 'inline-flex', alignItems: 'center', gap: 0.75,
            bgcolor: 'background.paper', border: 1, borderColor: 'divider',
            borderRadius: 1, px: 1, py: 0.25, fontSize: '0.7rem',
            color: 'text.secondary',
          }}>
            <CircularProgress size={12} /> Refreshing
          </Box>
        )}
      </Paper>
    </Box>
  );
}
